import { useState, useMemo } from 'react';
import Layout from '@/components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { useUser } from '@/contexts/UserContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { useSourcingRequests } from '@/hooks/useSourcingRequests';
import { useCustomers } from '@/hooks/useCustomers';
import { useWarehouses } from '@/hooks/useWarehouses';
import { useShipments } from '@/hooks/useShipments';
import { useTrips } from '@/hooks/useTrips';
import { PhotoUploader } from '@/components/PhotoUploader';
import { QRCodeDisplay, generateSourcingRequestQRValue } from '@/components/qr';
import { MultiItemSourcingForm } from '@/components/sourcing/MultiItemSourcingForm';
import { MultiItemShipmentForm } from '@/components/shipments/MultiItemShipmentForm';
import { PayWithWalletDialog } from '@/components/PayWithWalletDialog';
import {
  Plus,
  Search,
  Package,
  DollarSign,
  Clock,
  CheckCircle,
  XCircle,
  Edit,
  Trash2,
  FileText,
  Image as ImageIcon,
  Link as LinkIcon,
  AlertCircle,
  ShoppingCart,
  Truck,
  User,
  TrendingUp,
  Printer,
  Download,
  Mail,
  RotateCw,
  CreditCard
} from 'lucide-react';

interface SourcingRequestsProps {
  filter?: 'all' | 'pending' | 'quoted' | 'approved' | 'completed';
}

export default function SourcingRequests(props: SourcingRequestsProps) {
  const { user } = useUser();
  const { t } = useLanguage();
  const { filter = 'all' } = props;

  const { sourcingRequests: apiRequests, isLoading, createSourcingRequest, updateSourcingRequest, deleteSourcingRequest } = useSourcingRequests();
  const { customers: apiCustomers } = useCustomers();
  const { warehouses } = useWarehouses();
  const { createShipmentAsync } = useShipments();
  const { trips: apiTrips } = useTrips();

  // Convert API data
  const allRequests = (apiRequests || []).map((r: any) => ({
    ...r,
    photos: typeof r.photos === 'string' ? JSON.parse(r.photos || '[]') : (r.photos || []),
    items: typeof r.items === 'string' ? JSON.parse(r.items || '[]') : (r.items || []),
    referenceLinks: typeof r.reference_links === 'string' ? JSON.parse(r.reference_links || '[]') : (r.reference_links || []),
    createdAt: r.created_at || r.createdAt || new Date().toISOString(),
    updatedAt: r.updated_at || r.updatedAt || new Date().toISOString(),
  }));

  // Filter by customer role and warehouse access
  const requests = useMemo(() => {
    // Customer users see only their own requests
    if (user?.role === 'customer') {
      return allRequests.filter((r: any) => r.customerCode === user.customerCode || r.customer_code === user.customerCode);
    }
    
    // Warehouse users see only requests from customers assigned to their warehouse
    if (user?.role === 'warehouse' && user?.warehouseCode) {
      const warehouseCustomerCodes = (apiCustomers || [])
        .filter((c: any) => {
          const customerWarehouse = c.destination_warehouse || c.destinationWarehouse;
          return customerWarehouse === user.warehouseCode;
        })
        .map((c: any) => c.code);
      
      return allRequests.filter((r: any) => {
        const requestCustomerCode = r.customer_code || r.customerCode;
        return warehouseCustomerCodes.includes(requestCustomerCode);
      });
    }
    
    // Admin sees everything
    return allRequests;
  }, [allRequests, user, apiCustomers]);

  const [searchTerm, setSearchTerm] = useState('');
  const [customerFilter, setCustomerFilter] = useState<string>('all');
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isAdminCreateDialogOpen, setIsAdminCreateDialogOpen] = useState(false);
  const [selectedCustomerForAdmin, setSelectedCustomerForAdmin] = useState<string>('');
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isQuoteDialogOpen, setIsQuoteDialogOpen] = useState(false);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [isStatusDialogOpen, setIsStatusDialogOpen] = useState(false);
  const [isPrintDialogOpen, setIsPrintDialogOpen] = useState(false);
  const [isEditQuotationDialogOpen, setIsEditQuotationDialogOpen] = useState(false);
  const [isCreateShipmentDialogOpen, setIsCreateShipmentDialogOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState<any>(null);
  const [selectedRequestForShipment, setSelectedRequestForShipment] = useState<any>(null);
  const [showPayDialog, setShowPayDialog] = useState(false);
  const [selectedRequestForPayment, setSelectedRequestForPayment] = useState<any>(null);
  const [selectedForStatus, setSelectedForStatus] = useState<any>(null);
  const [newStatus, setNewStatus] = useState('');
  const [statusNote, setStatusNote] = useState('');
  const [amountPaid, setAmountPaid] = useState('');
  const [paymentNotes, setPaymentNotes] = useState('');

  // Multi-item form is now handled by MultiItemSourcingForm component
  // Removing old single-item state

  // Quote form state - per-item pricing
  const [quoteData, setQuoteData] = useState({
    itemPrices: {} as Record<number, string>, // item index -> quoted price
    sourcingFee: '',
    shippingEstimate: '',
    quoteNotes: '',
    quoteValidUntil: ''
  });

  // Get unique customers for filter dropdown
  const uniqueCustomers = useMemo(() => {
    const customerMap = new Map();
    allRequests.forEach((request: any) => {
      const code = request.customer_code || request.customerCode;
      const name = request.customer_name || request.customerName;
      if (code && !customerMap.has(code)) {
        customerMap.set(code, { code, name: name || code });
      }
    });
    return Array.from(customerMap.values()).sort((a, b) => a.code.localeCompare(b.code));
  }, [allRequests]);

  // Calculate statistics
  const stats = useMemo(() => {
    const totalRequests = requests.length;
    const pendingRequests = requests.filter((r: any) => r.status === 'Pending' || r.status === 'Under Review').length;
    const quotedRequests = requests.filter((r: any) => r.status === 'Quoted').length;
    const completedRequests = requests.filter((r: any) => r.status === 'Completed').length;
    
    // Calculate total sourcing revenue from paid requests
    // Count all statuses after payment received (payment received, order placed, in production, ready for shipment, shipment created, completed)
    const paidStatuses = ['Payment Received', 'Order Placed with Supplier', 'In Production', 'Ready for Shipment', 'Shipment Created', 'Completed'];
    
    const totalRevenue = requests
      .filter((r: any) => paidStatuses.includes(r.status))
      .reduce((sum: number, r: any) => {
        const grandTotal = parseFloat(r.grand_total || r.grandTotal || 0);
        return sum + grandTotal;
      }, 0);

    // Revenue this month
    const now = new Date();
    const thisMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);
    const revenueThisMonth = requests
      .filter((r: any) => {
        const createdDate = new Date(r.created_at || r.createdAt);
        return createdDate >= thisMonthStart && paidStatuses.includes(r.status);
      })
      .reduce((sum: number, r: any) => {
        const grandTotal = parseFloat(r.grand_total || r.grandTotal || 0);
        return sum + grandTotal;
      }, 0);

    return {
      totalRequests,
      pendingRequests,
      quotedRequests,
      completedRequests,
      totalRevenue,
      revenueThisMonth
    };
  }, [requests]);

  // Filter requests
  const filteredRequests = requests.filter((request: any) => {
    const searchLower = searchTerm.toLowerCase();
    const searchMatch = 
      (request.productName || request.product_name || '').toLowerCase().includes(searchLower) ||
      (request.requestNumber || request.request_number || '').toLowerCase().includes(searchLower) ||
      (request.description || '').toLowerCase().includes(searchLower) ||
      (request.customer_code || request.customerCode || '').toLowerCase().includes(searchLower) ||
      (request.customer_name || request.customerName || '').toLowerCase().includes(searchLower);

    let statusMatch = true;
    if (filter === 'pending') {
      statusMatch = request.status === 'Pending' || request.status === 'Under Review';
    } else if (filter === 'quoted') {
      statusMatch = request.status === 'Quoted';
    } else if (filter === 'approved') {
      statusMatch = request.status === 'Waiting for Payment' || request.status === 'Payment Received' || request.status === 'Order Placed with Supplier';
    } else if (filter === 'completed') {
      statusMatch = request.status === 'Completed';
    }

    // Customer filter (only applies for admin/warehouse)
    let customerMatch = true;
    if (user?.role !== 'customer' && customerFilter !== 'all') {
      const requestCustomerCode = request.customer_code || request.customerCode;
      customerMatch = requestCustomerCode === customerFilter;
    }

    return searchMatch && statusMatch && customerMatch;
  }).sort((a: any, b: any) => {
    const dateA = new Date(a.createdAt || a.created_at || '1970-01-01').getTime();
    const dateB = new Date(b.createdAt || b.created_at || '1970-01-01').getTime();
    return dateB - dateA;
  });

  // Handle create request with multi-item support
  const handleCreateRequest = (data: any) => {
    createSourcingRequest({
      customer_code: user?.customerCode,
      customer_name: user?.customerName,
      ...data
    });

    setIsCreateDialogOpen(false);
  };

  // Handle admin creating request on behalf of customer
  const handleAdminCreateRequest = (data: any) => {
    if (!selectedCustomerForAdmin) {
      alert('Please select a customer');
      return;
    }

    const customer = apiCustomers?.find((c: any) => c.code === selectedCustomerForAdmin);
    if (!customer) {
      alert('Customer not found');
      return;
    }

    createSourcingRequest({
      customer_code: customer.code,
      customer_name: customer.name,
      ...data
    });

    setIsAdminCreateDialogOpen(false);
    setSelectedCustomerForAdmin('');
  };

  // Handle send quote (admin only)
  const handleSendQuote = async () => {
    if (!selectedRequest) return;

    const sourcingFee = parseFloat(quoteData.sourcingFee) || 0;
    const shippingEstimate = parseFloat(quoteData.shippingEstimate) || 0;
    
    let quotedTotalAmount = 0;
    let updatedItems = selectedRequest.items;

    // Calculate total and update items with quoted prices
    if (selectedRequest.items && selectedRequest.items.length > 0) {
      updatedItems = selectedRequest.items.map((item: any, idx: number) => {
        const quotedPrice = parseFloat(quoteData.itemPrices[idx]) || 0;
        const qty = parseInt(item.quantity) || 0;
        quotedTotalAmount += quotedPrice * qty;
        
        return {
          ...item,
          quoted_price: quotedPrice
        };
      });
    } else {
      // Legacy single-item
      const quotedPrice = parseFloat(quoteData.itemPrices[0]) || 0;
      const qty = parseInt(selectedRequest.quantity) || 0;
      quotedTotalAmount = quotedPrice * qty;
    }
    
    const grandTotal = quotedTotalAmount + sourcingFee + shippingEstimate;

    updateSourcingRequest({
      request_number: selectedRequest.requestNumber || selectedRequest.request_number,
      items: updatedItems,
      quoted_total_amount: quotedTotalAmount,
      sourcing_fee: sourcingFee,
      shipping_estimate: shippingEstimate,
      grand_total: grandTotal,
      quote_notes: quoteData.quoteNotes,
      quote_valid_until: quoteData.quoteValidUntil,
      status: 'Quoted'
    });

    // Send email notification
    try {
      await fetch('https://sourcemygoods.com/api/public/send-quote-email.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          to: selectedRequest.customer_email || 'customer@email.com',
          customerName: selectedRequest.customer_name || selectedRequest.customerName,
          requestNumber: selectedRequest.requestNumber || selectedRequest.request_number,
          items: updatedItems,
          sourcingFee: sourcingFee,
          shippingEstimate: shippingEstimate,
          grandTotal: grandTotal,
          notes: quoteData.quoteNotes,
          validUntil: quoteData.quoteValidUntil
        })
      });
    } catch (error) {
      console.error('Failed to send quote email:', error);
    }

    setQuoteData({
      itemPrices: {},
      sourcingFee: '',
      shippingEstimate: '',
      quoteNotes: '',
      quoteValidUntil: ''
    });
    setIsQuoteDialogOpen(false);
  };

  // Handle status update
  const handleUpdateStatus = async () => {
    if (!selectedForStatus || !newStatus) return;

    // Special handling for "Shipment Created" - open shipment creation dialog
    if (newStatus === 'Shipment Created') {
      setSelectedRequestForShipment(selectedForStatus);
      setIsCreateShipmentDialogOpen(true);
      setIsStatusDialogOpen(false);
      return; // Don't update status yet - will update after shipment is created
    }

    // Prepare update data
    const updateData: any = {
      request_number: selectedForStatus.requestNumber || selectedForStatus.request_number,
      status: newStatus
    };

    // Add payment tracking if status is "Payment Received"
    if (newStatus === 'Payment Received') {
      const grandTotal = parseFloat(selectedForStatus.grand_total || selectedForStatus.grandTotal || 0);
      const paidAmount = parseFloat(amountPaid || '0');
      
      if (paidAmount <= 0) {
        alert('Please enter the amount received');
        return;
      }

      updateData.amount_paid = paidAmount;
      updateData.payment_date = new Date().toISOString();
      updateData.payment_notes = paymentNotes;
      
      // Determine payment status
      if (paidAmount >= grandTotal) {
        updateData.payment_status = 'Paid';
      } else {
        updateData.payment_status = 'Partial';
      }
    }

    updateSourcingRequest(updateData);

    // Send email notification for important status changes
    const notifyStatuses = ['Quoted', 'Payment Received', 'Order Placed with Supplier', 'Ready for Shipment', 'Completed'];
    if (notifyStatuses.includes(newStatus)) {
      try {
        // Calculate total item count for multi-item requests
        const items = selectedForStatus.items || [];
        const totalQuantity = items.length > 0 
          ? items.reduce((sum: number, item: any) => sum + (parseInt(item.quantity) || 0), 0)
          : (selectedForStatus.quantity || 0);
        
        await fetch('https://sourcemygoods.com/api/public/send-status-email.php', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            to: selectedForStatus.customer_email || 'customer@email.com',
            customerName: selectedForStatus.customer_name || selectedForStatus.customerName,
            requestNumber: selectedForStatus.requestNumber || selectedForStatus.request_number,
            productName: items.length > 0 
              ? `${items.length} item${items.length !== 1 ? 's' : ''}`
              : (selectedForStatus.productName || selectedForStatus.product_name),
            quantity: totalQuantity,
            quantityUnit: items.length > 0 ? 'items' : (selectedForStatus.quantityUnit || selectedForStatus.quantity_unit || 'pcs'),
            grandTotal: selectedForStatus.grandTotal || selectedForStatus.grand_total,
            quoteValidUntil: selectedForStatus.quoteValidUntil || selectedForStatus.quote_valid_until,
            newStatus: newStatus,
            note: statusNote
          })
        });
      } catch (error) {
        console.error('Failed to send status update email:', error);
      }
    }

    setSelectedForStatus(null);
    setNewStatus('');
    setStatusNote('');
    setAmountPaid('');
    setPaymentNotes('');
    setIsStatusDialogOpen(false);
  };

  // Open status dialog
  const openStatusDialog = (request: any) => {
    setSelectedForStatus(request);
    setNewStatus(request.status);
    setStatusNote('');
    setAmountPaid('');
    setPaymentNotes('');
    setIsStatusDialogOpen(true);
  };

  // Handle shipment creation from sourcing request
  const handleCreateShipmentFromRequest = async (shipmentData: any) => {
    if (!selectedRequestForShipment) return;

    const requestNumber = selectedRequestForShipment.requestNumber || selectedRequestForShipment.request_number;

    // Add sourcing request number to shipment data
    const dataWithSR = {
      ...shipmentData,
      sourcing_request_number: requestNumber
    };

    try {
      // Create the shipment and wait for response
      const response = await createShipmentAsync(dataWithSR);
      const shipmentNumber = response?.data?.shipment_number;

      // Update the sourcing request with shipment number and status
      updateSourcingRequest({
        request_number: requestNumber,
        status: 'Shipment Created',
        shipment_number: shipmentNumber || null
      });

      setIsCreateShipmentDialogOpen(false);
      setSelectedRequestForShipment(null);
    } catch (error) {
      console.error('Failed to create shipment:', error);
    }
  };

  // Handle approve quote (customer only)
  const handleApproveQuote = (request: any) => {
    updateSourcingRequest({
      request_number: request.requestNumber || request.request_number,
      status: 'Waiting for Payment'
    });
  };

  // Handle edit request (customer can edit items but not prices)
  const handleEditRequest = () => {
    if (!selectedRequest) return;

    updateSourcingRequest({
      request_number: selectedRequest.requestNumber || selectedRequest.request_number,
      product_name: selectedRequest.productName || selectedRequest.product_name,
      description: selectedRequest.description,
      quantity: selectedRequest.quantity,
      quantity_unit: selectedRequest.quantityUnit || selectedRequest.quantity_unit,
      special_requirements: selectedRequest.specialRequirements || selectedRequest.special_requirements,
      expected_delivery_date: selectedRequest.expectedDeliveryDate || selectedRequest.expected_delivery_date
    });

    setIsEditDialogOpen(false);
  };

  // Handle edit quotation (full edit with items/pictures/prices)
  const handleEditQuotation = (data: any) => {
    if (!selectedRequest) return;

    updateSourcingRequest({
      request_number: selectedRequest.requestNumber || selectedRequest.request_number,
      items: JSON.stringify(data.items),
      notes: data.notes,
      currency: 'USD' // Always USD now
    });

    setIsEditQuotationDialogOpen(false);
  };

  // Handle delete request
  const handleDeleteRequest = (requestNumber: string) => {
    if (confirm('Are you sure you want to delete this request?')) {
      deleteSourcingRequest(requestNumber);
    }
  };

  // Get status color
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Pending': return 'bg-gray-100 text-gray-800';
      case 'Under Review': return 'bg-blue-100 text-blue-800';
      case 'Quoted': return 'bg-purple-100 text-purple-800';
      case 'Waiting for Payment': return 'bg-yellow-100 text-yellow-800';
      case 'Payment Received': return 'bg-green-100 text-green-800';
      case 'Order Placed with Supplier': return 'bg-indigo-100 text-indigo-800';
      case 'In Production': return 'bg-orange-100 text-orange-800';
      case 'Ready for Shipment': return 'bg-cyan-100 text-cyan-800';
      case 'Shipment Created': return 'bg-teal-100 text-teal-800';
      case 'Completed': return 'bg-green-100 text-green-800';
      case 'Cancelled': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // Get status icon
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'Pending':
      case 'Under Review': return <Clock className="h-4 w-4" />;
      case 'Quoted': return <FileText className="h-4 w-4" />;
      case 'Waiting for Payment': return <DollarSign className="h-4 w-4" />;
      case 'Payment Received':
      case 'Order Placed with Supplier': return <ShoppingCart className="h-4 w-4" />;
      case 'In Production': return <Package className="h-4 w-4" />;
      case 'Ready for Shipment':
      case 'Shipment Created': return <Truck className="h-4 w-4" />;
      case 'Completed': return <CheckCircle className="h-4 w-4" />;
      case 'Cancelled': return <XCircle className="h-4 w-4" />;
      default: return <AlertCircle className="h-4 w-4" />;
    }
  };

  // Open quote dialog
  const openQuoteDialog = (request: any) => {
    setSelectedRequest(request);
    
    // Initialize per-item prices from target prices or existing quotes
    const itemPrices: Record<number, string> = {};
    if (request.items && request.items.length > 0) {
      request.items.forEach((item: any, idx: number) => {
        // Use existing quoted price, or target price as starting point, or empty
        itemPrices[idx] = item.quotedPrice?.toString() || item.quoted_price?.toString() || item.targetPrice?.toString() || item.target_price?.toString() || '';
      });
    }
    
    setQuoteData({
      itemPrices,
      sourcingFee: request.sourcingFee?.toString() || request.sourcing_fee?.toString() || '',
      shippingEstimate: request.shippingEstimate?.toString() || request.shipping_estimate?.toString() || '',
      quoteNotes: request.quoteNotes || request.quote_notes || '',
      quoteValidUntil: request.quoteValidUntil || request.quote_valid_until || ''
    });
    setIsQuoteDialogOpen(true);
  };

  // Open edit dialog
  const openEditDialog = (request: any) => {
    setSelectedRequest({
      ...request,
      productName: request.productName || request.product_name,
      quantityUnit: request.quantityUnit || request.quantity_unit,
      specialRequirements: request.specialRequirements || request.special_requirements,
      expectedDeliveryDate: request.expectedDeliveryDate || request.expected_delivery_date
    });
    setIsEditDialogOpen(true);
  };

  // Open edit quotation dialog (full edit with items/pictures)
  const openEditQuotationDialog = (request: any) => {
    setSelectedRequest(request);
    setIsEditQuotationDialogOpen(true);
  };

  // Open view dialog
  const openViewDialog = (request: any) => {
    setSelectedRequest(request);
    setIsViewDialogOpen(true);
  };

  // Open print dialog
  const openPrintDialog = (request: any) => {
    setSelectedRequest(request);
    setIsPrintDialogOpen(true);
  };

  // Print/Download request as PDF
  const handlePrintRequest = () => {
    if (!selectedRequest) return;

    const requestNumber = selectedRequest.requestNumber || selectedRequest.request_number;
    const customerName = selectedRequest.customer_name || selectedRequest.customerName;
    const items = selectedRequest.items || [];
    const hasItems = items.length > 0;
    const currency = selectedRequest.currency || 'USD';
    const currencySymbol = '$'; // Always USD

    // Generate QR code as base64 image
    const qrValue = generateSourcingRequestQRValue(selectedRequest);
    const canvas = document.createElement('canvas');
    
    import('qrcode').then(QRCode => {
      QRCode.default.toCanvas(canvas, qrValue, { width: 150, margin: 1 }, (error) => {
        if (error) console.error(error);
        const qrImageUrl = canvas.toDataURL();

    const logoUrl = window.location.origin + '/logo-full.jpg';
    
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;
    
    // Create printable HTML content
    const printContent = `
  <!DOCTYPE html>
  <html>
    <head>
      <meta charset="UTF-8">
      <title>Sourcing Request - ${requestNumber}</title>
      <style>
      @page {
        size: A4 portrait;
        margin: 10mm;
      }
      * { box-sizing: border-box; margin: 0; padding: 0; }
      body {
        font-family: Arial, Helvetica, sans-serif;
        font-size: 9pt;
        line-height: 1.4;
        color: #1a1a2e;
        background: #fff;
        padding: 12px;
      }

      /* HEADER */
      .doc-header {
        display: flex;
        align-items: center;
        gap: 12px;
        padding-bottom: 8px;
        margin-bottom: 12px;
        border-bottom: 2px solid #1e3a6e;
      }
      .doc-header img.logo {
        height: 50px;
        width: auto;
        object-fit: contain;
        flex-shrink: 0;
      }
      .doc-header .vertical-line {
        width: 2px;
        height: 50px;
        background: #1e3a6e;
        flex-shrink: 0;
      }
      .doc-header .company-info {
        flex: 1;
        font-size: 8pt;
        color: #555;
        line-height: 1.5;
      }

      /* TITLE BAND with Gradient */
      .title-band {
        background: linear-gradient(135deg, #1e3a6e 0%, #2563eb 100%);
        color: #fff;
        padding: 8px 12px;
        border-radius: 4px;
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 12px;
      }
      .title-band h1 { font-size: 14pt; letter-spacing: 1px; margin: 0; }
      .title-band .request-meta { text-align: right; font-size: 8pt; line-height: 1.5; }
      .title-band .request-meta strong { font-size: 10pt; display: block; }

      /* INFO BOXES */
      .info-row {
        display: flex;
        gap: 10px;
        margin-bottom: 12px;
      }
      .info-box {
        flex: 1;
        border: 1px solid #dee2e6;
        border-radius: 4px;
        padding: 8px 10px;
        background: #f8f9fa;
        page-break-inside: avoid;
      }
      .info-box h3 {
        font-size: 8pt;
        text-transform: uppercase;
        color: #1e3a6e;
        border-bottom: 1px solid #1e3a6e;
        padding-bottom: 3px;
        margin-bottom: 6px;
        letter-spacing: 0.5px;
      }
      .info-box p { font-size: 8.5pt; margin-bottom: 2px; }

      /* QR CODE PANEL */
      .qr-panel {
        text-align: center;
        border: 1px solid #dee2e6;
        border-radius: 4px;
        padding: 8px;
        background: #f8f9fa;
        width: 110px;
        flex-shrink: 0;
      }
      .qr-panel img { width: 90px; height: 90px; }
      .qr-panel p { font-size: 6.5pt; color: #888; margin-top: 4px; }

      /* SECTION TITLE */
      .section-title {
        font-size: 9pt;
        font-weight: bold;
        color: #fff;
        background: linear-gradient(135deg, #1e3a6e 0%, #2563eb 100%);
        padding: 4px 8px;
        border-radius: 3px;
        margin: 12px 0 6px 0;
      }

      /* TABLES */
      table {
        width: 100%;
        border-collapse: collapse;
        font-size: 7.5pt;
        page-break-inside: auto;
      }
      thead tr {
        background: #1e3a6e;
        color: #fff;
      }
      th {
        padding: 5px 6px;
        text-align: left;
        font-weight: 600;
        font-size: 8pt;
      }
      td {
        padding: 4px 6px;
        border-bottom: 1px solid #e9ecef;
      }
      tbody tr:nth-child(even) { background: #f8f9fa; }
      td.right, th.right { text-align: right; }
      tr { page-break-inside: avoid; }
      
      /* Photo in table */
      .item-photo {
        width: 40px;
        height: 40px;
        object-fit: cover;
        border-radius: 3px;
        border: 1px solid #dee2e6;
      }

      /* TOTALS TABLE */
      .totals-wrap {
        display: flex;
        justify-content: flex-end;
        margin-top: 10px;
        page-break-inside: avoid;
      }
      .totals-table {
        width: 240px;
        border: 1px solid #dee2e6;
        border-radius: 4px;
        overflow: hidden;
      }
      .totals-table td {
        padding: 5px 8px;
        border-bottom: 1px solid #e9ecef;
        font-size: 8.5pt;
      }
      .totals-table .label-col { color: #555; }
      .totals-table .val-col { text-align: right; font-weight: 600; }
      .totals-table .grand-total td {
        background: linear-gradient(135deg, #1e3a6e 0%, #2563eb 100%);
        color: #fff;
        font-size: 10pt;
        font-weight: bold;
        border-bottom: none;
      }

      /* FOOTER */
      .doc-footer {
        margin-top: 20px;
        padding-top: 8px;
        border-top: 1px solid #dee2e6;
        text-align: center;
        font-size: 7.5pt;
        color: #888;
        line-height: 1.6;
        page-break-inside: avoid;
      }
      .doc-footer strong { color: #1e3a6e; }

      @media print {
        body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      }
    </style>
</head>
<body>

    <!-- HEADER -->
    <div class="doc-header">
      <img class="logo" src="${logoUrl}" alt="SourceMyGoods" onerror="this.style.display='none'"/>
      <div class="vertical-line"></div>
      <div class="company-info">
        Phone: +86 130 4806 4436<br/>
        Email: sales@sourcemygoods.com<br/>
        Address: Office 407, No.6, Yuanjing Road, Tianhe District, Guangzhou, China
      </div>
    </div>

    <!-- TITLE BAND -->
    <div class="title-band">
      <h1>SOURCING REQUEST ${(selectedRequest.grand_total || selectedRequest.grandTotal) && parseFloat(selectedRequest.grand_total || selectedRequest.grandTotal || 0) > 0 ? '& QUOTATION' : ''}</h1>
      <div class="request-meta">
        <strong>${requestNumber}</strong>
        <span>${new Date().toLocaleDateString()}</span>
      </div>
    </div>

    <!-- INFO ROW -->
    <div class="info-row">
      <div class="info-box">
        <h3>Customer Information</h3>
        <p><strong>${customerName}</strong></p>
        <p>Code: ${selectedRequest.customer_code || selectedRequest.customerCode}</p>
        ${selectedRequest.customer_phone ? `<p>Phone: ${selectedRequest.customer_phone}</p>` : ''}
        ${selectedRequest.customer_email ? `<p>Email: ${selectedRequest.customer_email}</p>` : ''}
        ${selectedRequest.customer_address ? `<p>Address: ${selectedRequest.customer_address}</p>` : ''}
      </div>
      <div class="info-box">
        <h3>Request Details</h3>
        <p><strong>Status:</strong> ${selectedRequest.status}</p>
        <p><strong>Created:</strong> ${new Date(selectedRequest.created_at || selectedRequest.createdAt).toLocaleDateString()}</p>
        ${selectedRequest.expected_delivery_date || selectedRequest.expectedDeliveryDate ? `<p><strong>Expected Delivery:</strong> ${selectedRequest.expected_delivery_date || selectedRequest.expectedDeliveryDate}</p>` : ''}
        ${selectedRequest.shipment_number || selectedRequest.shipmentNumber ? `<p><strong>Shipment:</strong> <span style="color: #059669; font-weight: bold;">${selectedRequest.shipment_number || selectedRequest.shipmentNumber}</span></p>` : ''}
      </div>
      <div class="qr-panel">
        <img src="${qrImageUrl}" alt="QR Code" />
        <p>SCAN FOR DETAILS</p>
      </div>
    </div>

    <!-- ITEMS TABLE -->
    ${hasItems ? `
      <div class="section-title">Requested Items</div>
      <table>
        <thead>
          <tr>
            <th>No.</th>
            <th>Photo</th>
            <th>Item</th>
            <th>Description</th>
            <th>Unit</th>
            <th class="right">Price/Unit</th>
            <th class="right">Qty</th>
            <th class="right">Total</th>
          </tr>
        </thead>
        <tbody>
          ${items.map((item, idx) => {
            const quotedPrice = parseFloat(item.quoted_price || 0);
            const qty = parseInt(item.quantity || 0);
            const total = quotedPrice * qty;
            const firstPhoto = item.photos && item.photos.length > 0 ? item.photos[0] : '';
            
            return `
            <tr>
              <td>${idx + 1}</td>
              <td>
                ${firstPhoto ? `<img src="https://sourcemygoods.com${firstPhoto}" class="item-photo" onerror="this.style.display='none'"/>` : '—'}
              </td>
              <td><strong>${item.productName || item.product_name}</strong></td>
              <td style="max-width:180px; font-size:8.5pt;">${item.description || '—'}</td>
              <td>${item.quantityUnit || item.quantity_unit}</td>
              <td class="right">${quotedPrice > 0 ? currencySymbol + quotedPrice.toFixed(2) : '—'}</td>
              <td class="right"><strong>${qty}</strong></td>
              <td class="right"><strong>${total > 0 ? currencySymbol + total.toFixed(2) : '—'}</strong></td>
            </tr>
            `;
          }).join('')}
        </tbody>
      </table>
    ` : `
      <div class="section-title">Product Details</div>
      <div class="info-box">
        <p><strong>Product:</strong> ${selectedRequest.productName || selectedRequest.product_name}</p>
        <p><strong>Description:</strong> ${selectedRequest.description || '—'}</p>
        <p><strong>Quantity:</strong> ${selectedRequest.quantity} ${selectedRequest.quantityUnit || selectedRequest.quantity_unit}</p>
      </div>
    `}

    ${selectedRequest.specialRequirements || selectedRequest.special_requirements ? `
      <div class="section-title">Special Requirements</div>
      <div class="info-box">
        <p>${selectedRequest.specialRequirements || selectedRequest.special_requirements}</p>
      </div>
    ` : ''}

    <!-- QUOTATION (if quoted) -->
    ${(selectedRequest.grand_total || selectedRequest.grandTotal) && parseFloat(selectedRequest.grand_total || selectedRequest.grandTotal || 0) > 0 ? (() => {
      const grandTotal = parseFloat(selectedRequest.grand_total || selectedRequest.grandTotal || 0);
      const amountPaid = parseFloat(selectedRequest.amount_paid || selectedRequest.amountPaid || 0);
      const amountDue = grandTotal - amountPaid;
      const paymentStatus = selectedRequest.payment_status || selectedRequest.paymentStatus || 'Pending';
      
      return `
      <div class="section-title">Quotation (${currency})</div>
      <div class="totals-wrap">
        <table class="totals-table">
          <tr>
            <td class="label-col">Total Product Cost</td>
            <td class="val-col">${currencySymbol} ${parseFloat(selectedRequest.quoted_total_amount || selectedRequest.quotedTotalAmount || 0).toFixed(2)}</td>
          </tr>
          <tr>
            <td class="label-col">Sourcing Fee</td>
            <td class="val-col">${currencySymbol} ${parseFloat(selectedRequest.sourcing_fee || selectedRequest.sourcingFee || 0).toFixed(2)}</td>
          </tr>
          <tr>
            <td class="label-col">Shipping Estimate</td>
            <td class="val-col">${currencySymbol} ${parseFloat(selectedRequest.shipping_estimate || selectedRequest.shippingEstimate || 0).toFixed(2)}</td>
          </tr>
          <tr class="grand-total">
            <td class="label-col">GRAND TOTAL</td>
            <td class="val-col">${currencySymbol} ${grandTotal.toFixed(2)}</td>
          </tr>
          ${amountPaid > 0 ? `
          <tr style="background: #dcfce7;">
            <td class="label-col" style="color: #166534;">Amount Paid</td>
            <td class="val-col" style="color: #166534;">${currencySymbol} ${amountPaid.toFixed(2)}</td>
          </tr>
          <tr style="background: ${amountDue > 0 ? '#fef3c7' : '#dcfce7'};">
            <td class="label-col" style="color: ${amountDue > 0 ? '#92400e' : '#166534'}; font-weight: bold;">Amount Due</td>
            <td class="val-col" style="color: ${amountDue > 0 ? '#92400e' : '#166534'}; font-weight: bold;">${currencySymbol} ${amountDue.toFixed(2)}</td>
          </tr>
          ` : ''}
        </table>
      </div>
      ${amountPaid > 0 ? `
        <div style="text-align:center; margin-top:12px; padding:8px; border-radius:4px; background: ${paymentStatus === 'Paid' ? '#dcfce7' : '#fef3c7'}; color: ${paymentStatus === 'Paid' ? '#166534' : '#92400e'}; font-weight:600; font-size:10pt;">
          Payment Status: ${paymentStatus}${paymentStatus === 'Partial' ? ` (${currencySymbol}${amountPaid.toFixed(2)} paid, ${currencySymbol}${amountDue.toFixed(2)} remaining)` : ''}
        </div>
      ` : ''}
      ${selectedRequest.quoteNotes || selectedRequest.quote_notes ? `
        <div class="info-box" style="margin-top:12px;">
          <h3>Quote Notes</h3>
          <p>${selectedRequest.quoteNotes || selectedRequest.quote_notes}</p>
        </div>
      ` : ''}
      ${selectedRequest.quoteValidUntil || selectedRequest.quote_valid_until ? `
        <div style="text-align:center; margin-top:12px; color:#dc2626; font-weight:600; font-size:10pt;">
          Quote Valid Until: ${selectedRequest.quoteValidUntil || selectedRequest.quote_valid_until}
        </div>
      ` : ''}
    `;
    })() : ''}

    <!-- FOOTER -->
    <div class="doc-footer">
      <strong>SourceMyGoods</strong> &nbsp;|&nbsp; Professional Sourcing, Inspection & Global Shipping<br/>
      Operated by Trust Exchange International Trading LTD<br/>
      Generated on ${new Date().toLocaleDateString()} at ${new Date().toLocaleTimeString()}
    </div>

    <script>window.onload = function() { window.print(); }</script>
  </body>
  </html>
    `;

        printWindow.document.write(printContent);
        printWindow.document.close();
      });
    });
  };

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">{t('sourcingRequests')}</h1>
            <p className="text-gray-600">{t('requestProductsAndReceiveQuotes')}</p>
          </div>

          {user?.role === 'customer' && (
            <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
              <DialogTrigger asChild>
                <Button>
                  <Plus className="h-4 w-4 mr-2" />
                  {t('newRequest')}
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-[800px] max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>{t('createSourcingRequest')}</DialogTitle>
                  <DialogDescription>
                    {t('tellUsWhatYouNeed')}
                  </DialogDescription>
                </DialogHeader>
                <div className="py-4">
                  <MultiItemSourcingForm
                    onSubmit={handleCreateRequest}
                    isSubmitting={false}
                  />
                </div>
              </DialogContent>
            </Dialog>
          )}

          {/* Admin Create Request Button */}
          {(user?.role === 'admin' || user?.role === 'warehouse') && (
            <Dialog open={isAdminCreateDialogOpen} onOpenChange={setIsAdminCreateDialogOpen}>
              <DialogTrigger asChild>
                <Button>
                  <Plus className="h-4 w-4 mr-2" />
                  {t('newRequest')}
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-[800px] max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Create Sourcing Request (Admin)</DialogTitle>
                  <DialogDescription>
                    Create a sourcing request on behalf of a customer
                  </DialogDescription>
                </DialogHeader>
                <div className="py-4 space-y-4">
                  {/* Customer Selection */}
                  <div className="space-y-2 pb-4 border-b">
                    <Label htmlFor="admin-customer-select">Select Customer *</Label>
                    <Select value={selectedCustomerForAdmin} onValueChange={setSelectedCustomerForAdmin}>
                      <SelectTrigger id="admin-customer-select">
                        <SelectValue placeholder="Choose a customer..." />
                      </SelectTrigger>
                      <SelectContent>
                        {(() => {
                          // Filter customers for warehouse managers
                          let availableCustomers = apiCustomers || [];
                          if (user?.role === 'warehouse' && user?.warehouseCode) {
                            availableCustomers = availableCustomers.filter((c: any) => {
                              const customerWarehouse = c.destination_warehouse || c.destinationWarehouse;
                              return customerWarehouse === user.warehouseCode;
                            });
                          }
                          
                          return availableCustomers.map((customer: any) => (
                            <SelectItem key={customer.code} value={customer.code}>
                              {customer.code} - {customer.name}
                            </SelectItem>
                          ));
                        })()}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-gray-500">
                      {user?.role === 'warehouse' 
                        ? 'Only customers assigned to your warehouse are shown'
                        : 'The request will be created under the selected customer\'s account'}
                    </p>
                  </div>

                  {/* Request Form */}
                  {selectedCustomerForAdmin && (
                    <MultiItemSourcingForm
                      onSubmit={handleAdminCreateRequest}
                      isSubmitting={false}
                    />
                  )}

                  {!selectedCustomerForAdmin && (
                    <div className="text-center py-8 text-gray-500">
                      Please select a customer to continue
                    </div>
                  )}
                </div>
              </DialogContent>
            </Dialog>
          )}
        </div>

        {/* Statistics Dashboard */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">{t('totalRequests')}</CardTitle>
              <ShoppingCart className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.totalRequests}</div>
              <p className="text-xs text-muted-foreground">{t('allTimeRequests')}</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">{t('pending')}</CardTitle>
              <Clock className="h-4 w-4 text-orange-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-orange-600">{stats.pendingRequests}</div>
              <p className="text-xs text-muted-foreground">{t('awaitingQuote')}</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">{t('quoted')}</CardTitle>
              <FileText className="h-4 w-4 text-purple-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-purple-600">{stats.quotedRequests}</div>
              <p className="text-xs text-muted-foreground">{t('awaitingApproval')}</p>
            </CardContent>
          </Card>

          {/* Revenue Card - Only for admin/warehouse */}
          {user?.role !== 'customer' && (
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">{t('sourcingRevenue')}</CardTitle>
                <TrendingUp className="h-4 w-4 text-green-600" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-green-600">${stats.totalRevenue.toFixed(2)}</div>
                <p className="text-xs text-muted-foreground">{t('thisMonth')}: ${stats.revenueThisMonth.toFixed(2)}</p>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Search and Filters */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
            <Input
              placeholder={user?.role === 'customer' ? t('searchByRequestNumber') : t('searchByRequestNumberCustomer')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>

          {/* Customer Filter - Only for admin/warehouse */}
          {user?.role !== 'customer' && uniqueCustomers.length > 0 && (
            <Select value={customerFilter} onValueChange={setCustomerFilter}>
              <SelectTrigger>
                <SelectValue placeholder={t('filterByCustomer')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('allCustomers')} ({requests.length})</SelectItem>
                {uniqueCustomers.map((customer: any) => {
                  const count = requests.filter((r: any) => 
                    (r.customer_code || r.customerCode) === customer.code
                  ).length;
                  return (
                    <SelectItem key={customer.code} value={customer.code}>
                      {customer.name} ({customer.code}) - {count} {count !== 1 ? t('requests') : t('request')}
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          )}
        </div>

        {/* Requests Grid */}
        {isLoading ? (
          <div className="text-center py-12">
            <p className="text-gray-500">{t('loadingRequests')}</p>
          </div>
        ) : filteredRequests.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Package className="h-12 w-12 mx-auto text-gray-400 mb-4" />
              <p className="text-gray-500">{t('noSourcingRequestsFound')}</p>
              {user?.role === 'customer' && (
                <Button onClick={() => setIsCreateDialogOpen(true)} variant="outline" className="mt-4">
                  <Plus className="h-4 w-4 mr-2" />
                  {t('createYourFirstRequest')}
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredRequests.map((request: any) => (
              <Card key={request.id} className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <CardTitle className="text-lg">
                        {request.productName || request.product_name}
                      </CardTitle>
                      <CardDescription className="text-xs mt-1 space-y-1">
                        <div>{request.requestNumber || request.request_number}</div>
                        {/* Show customer info for admin/warehouse */}
                        {user?.role !== 'customer' && (
                          <div className="flex items-center gap-1 text-blue-600">
                            <User className="h-3 w-3" />
                            <span>
                              {request.customer_name || request.customerName || 'Unknown'} 
                              {' '}({request.customer_code || request.customerCode || 'N/A'})
                            </span>
                          </div>
                        )}
                      </CardDescription>
                    </div>
                    <div className="flex flex-col gap-1">
                      <Badge className={getStatusColor(request.status)}>
                        <span className="flex items-center gap-1">
                          {getStatusIcon(request.status)}
                          {request.status}
                        </span>
                      </Badge>
                      {/* Payment Status Badge - Always Visible */}
                      {request.payment_status && request.payment_status !== 'Pending' && (
                        <Badge className={
                          request.payment_status === 'Paid' ? 'bg-green-100 text-green-800' :
                          request.payment_status === 'Partial' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-gray-100 text-gray-800'
                        }>
                          <DollarSign className="h-3 w-3 mr-1" />
                          {request.payment_status === 'Partial' 
                            ? `Partial ($${parseFloat(request.amount_paid || 0).toFixed(2)})` 
                            : request.payment_status}
                        </Badge>
                      )}
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {/* Product photo preview */}
                    {request.photos && request.photos.length > 0 && (
                      <div className="relative h-32 w-full rounded-lg overflow-hidden bg-gray-100">
                        <img
                          src={`https://sourcemygoods.com${request.photos[0]}`}
                          alt={request.productName || request.product_name}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.style.display = 'none';
                          }}
                        />
                        {request.photos.length > 1 && (
                          <Badge className="absolute bottom-2 right-2 bg-black/70 text-white">
                            <ImageIcon className="h-3 w-3 mr-1" />
                            +{request.photos.length - 1}
                          </Badge>
                        )}
                      </div>
                    )}

                    <p className="text-sm text-gray-600 line-clamp-2">
                      {request.description}
                    </p>

                    {/* Show items count if multi-item */}
                    {request.items && request.items.length > 0 ? (
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-500">{t('items')}:</span>
                        <span className="font-medium">
                          {request.items.length} {request.items.length !== 1 ? t('items') : t('item')}
                        </span>
                      </div>
                    ) : (
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-500">{t('quantity')}:</span>
                        <span className="font-medium">
                          {request.quantity} {request.quantityUnit || request.quantity_unit}
                        </span>
                      </div>
                    )}

                    {/* Show reference links indicator */}
                    {request.referenceLinks && request.referenceLinks.length > 0 && (
                      <div className="flex items-center gap-1 text-xs text-gray-500">
                        <LinkIcon className="h-3 w-3" />
                        {request.referenceLinks.length} {request.referenceLinks.length !== 1 ? t('referenceLinks') : t('referenceLink')}
                      </div>
                    )}

                    {(request.status === 'Quoted' || request.status === 'Waiting for Payment' || request.status === 'Payment Received' || request.status === 'Order Placed with Supplier' || request.status === 'In Production' || request.status === 'Ready for Shipment' || request.status === 'Shipment Created' || request.status === 'Completed') && request.grand_total && (
                      <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-gray-600">{t('grandTotal')} ({request.currency || 'USD'}):</span>
                          <span className="font-bold text-purple-900">
                            $
                            {parseFloat(request.grandTotal || request.grand_total || 0).toFixed(2)}
                          </span>
                        </div>
                      </div>
                    )}

                    {/* Shipment Number Reference */}
                    {request.shipment_number && (
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-2">
                        <div className="flex items-center gap-2 text-sm">
                          <Truck className="h-4 w-4 text-blue-600" />
                          <span className="text-gray-600">Shipment:</span>
                          <span className="font-semibold text-blue-900">{request.shipment_number}</span>
                        </div>
                      </div>
                    )}

                    <div className="flex flex-wrap gap-2 pt-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openViewDialog(request)}
                        title={t('view')}
                      >
                        <FileText className="h-4 w-4" />
                      </Button>

                      {/* Customer actions */}
                      {user?.role === 'customer' && (
                        <>
                          {/* Pay with Wallet button - when waiting for payment */}
                          {request.status === 'Waiting for Payment' && (
                            <Button
                              variant="default"
                              size="sm"
                              onClick={() => {
                                setSelectedRequestForPayment(request);
                                setShowPayDialog(true);
                              }}
                              className="bg-green-600 hover:bg-green-700"
                              title="Pay with Wallet"
                            >
                              <CreditCard className="h-4 w-4 mr-1" />
                              Pay
                            </Button>
                          )}
                        
                          {/* Edit button - only before approval */}
                          {request.status === 'Quoted' && (
                            <>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => openEditDialog(request)}
                                title={t('edit')}
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="default"
                                size="sm"
                                onClick={() => handleApproveQuote(request)}
                                className="bg-green-600 hover:bg-green-700"
                                title={t('approve')}
                              >
                                <CheckCircle className="h-4 w-4" />
                              </Button>
                            </>
                          )}

                          {/* Delete button - only before payment */}
                          {(request.status === 'Pending' || request.status === 'Under Review' || request.status === 'Quoted') && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDeleteRequest(request.requestNumber || request.request_number)}
                              className="text-red-600 hover:text-red-700"
                              title={t('deleteRequest')}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </>
                      )}

                      {/* Admin actions */}
                      {(user?.role === 'admin' || user?.role === 'warehouse') && (
                        <>
                          {(request.status === 'Pending' || request.status === 'Under Review') && (
                            <Button
                              variant="default"
                              size="sm"
                              onClick={() => openQuoteDialog(request)}
                              title={t('quote')}
                            >
                              <DollarSign className="h-4 w-4" />
                            </Button>
                          )}
                          {/* Edit Quotation Details button - full edit with items/pictures */}
                          {(request.status === 'Quoted' || request.status === 'Waiting for Payment' || request.status === 'Payment Received') && request.grand_total && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => openEditQuotationDialog(request)}
                              title="Edit Quotation Details"
                              className="border-purple-300 text-purple-700 hover:bg-purple-50"
                            >
                              <Edit className="h-4 w-4 mr-1" />
                              Edit Quotation
                            </Button>
                          )}
                          {/* Status Update button - shown for non-completed requests */}
                          {request.status !== 'Completed' && request.status !== 'Cancelled' && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => openStatusDialog(request)}
                              title={t('updateStatus')}
                            >
                              <RotateCw className="h-4 w-4" />
                            </Button>
                          )}
                          {/* Delete button - admin can delete anytime */}
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDeleteRequest(request.requestNumber || request.request_number)}
                            className="text-red-600 hover:text-red-700"
                            title={t('deleteRequest')}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </>
                      )}

                      {/* Print button - available at all stages */}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openPrintDialog(request)}
                        title={t('printDownload')}
                      >
                        <Printer className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* View Dialog */}
        <Dialog open={isViewDialogOpen} onOpenChange={setIsViewDialogOpen}>
          <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{selectedRequest?.productName || selectedRequest?.product_name}</DialogTitle>
              <DialogDescription>
                Request #{selectedRequest?.requestNumber || selectedRequest?.request_number}
              </DialogDescription>
            </DialogHeader>
            {selectedRequest && (
              <div className="space-y-4">
                {/* Customer Info - shown for admin/warehouse */}
                {user?.role !== 'customer' && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <Label className="text-sm font-semibold text-blue-900">Customer</Label>
                    <div className="flex items-center gap-2 mt-1">
                      <User className="h-4 w-4 text-blue-600" />
                      <span className="text-sm font-medium text-blue-900">
                        {selectedRequest.customer_name || selectedRequest.customerName || 'Unknown'}
                      </span>
                      <Badge variant="secondary" className="text-xs">
                        {selectedRequest.customer_code || selectedRequest.customerCode || 'N/A'}
                      </Badge>
                    </div>
                  </div>
                )}

                <div>
                  <Label className="text-sm font-semibold">Status</Label>
                  <Badge className={`${getStatusColor(selectedRequest.status)} mt-1`}>
                    {selectedRequest.status}
                  </Badge>
                </div>

                {/* Items List - Show each item with ALL its details */}
                {selectedRequest.items && selectedRequest.items.length > 0 ? (
                  <div className="space-y-4">
                    <Label className="text-lg font-semibold">Requested Items ({selectedRequest.items.length})</Label>
                    
                    {selectedRequest.items.map((item: any, index: number) => (
                      <Card key={index} className="border-2">
                        <CardContent className="pt-4 space-y-3">
                          {/* Item Header */}
                          <div className="flex items-start justify-between">
                            <Badge className="text-base font-semibold px-3 py-1">
                              Item #{index + 1}
                            </Badge>
                            <div className="text-right">
                              <div className="text-sm font-medium text-gray-900">
                                {item.quantity || item.quantity || 0} {item.quantityUnit || item.quantity_unit || 'pcs'}
                              </div>
                              {item.targetPrice || item.target_price ? (
                                <div className="text-xs text-gray-500">
                                  Target: ${parseFloat(item.targetPrice || item.target_price).toFixed(2)}
                                </div>
                              ) : null}
                              {item.quoted_price || item.quotedPrice ? (
                                <div className="text-xs font-semibold text-purple-700">
                                  Quoted: ${parseFloat(item.quoted_price || item.quotedPrice).toFixed(2)}
                                </div>
                              ) : null}
                            </div>
                          </div>

                          {/* Product Name */}
                          <div>
                            <Label className="text-xs text-gray-500">Product Name</Label>
                            <p className="text-sm font-semibold text-gray-900 mt-1">
                              {item.productName || item.product_name}
                            </p>
                          </div>

                          {/* Description */}
                          <div>
                            <Label className="text-xs text-gray-500">Description</Label>
                            <p className="text-sm text-gray-700 mt-1 whitespace-pre-wrap">
                              {item.description}
                            </p>
                          </div>

                          {/* Product Photos for this item */}
                          {item.photos && item.photos.length > 0 && (
                            <div>
                              <Label className="text-xs text-gray-500">Photos ({item.photos.length})</Label>
                              <div className="grid grid-cols-4 gap-2 mt-2">
                                {item.photos.map((photo: string, photoIndex: number) => (
                                  <a
                                    key={photoIndex}
                                    href={`https://sourcemygoods.com${photo}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="block"
                                  >
                                    <img
                                      src={`https://sourcemygoods.com${photo}`}
                                      alt={`Item ${index + 1} Photo ${photoIndex + 1}`}
                                      className="w-full h-20 object-cover rounded border hover:border-blue-500 transition-colors cursor-pointer"
                                      onError={(e) => {
                                        const target = e.target as HTMLImageElement;
                                        target.style.display = 'none';
                                      }}
                                    />
                                  </a>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Reference Links for this item */}
                          {item.referenceLinks && item.referenceLinks.length > 0 || item.reference_links && item.reference_links.length > 0 && (
                            <div>
                              <Label className="text-xs text-gray-500">Reference Links</Label>
                              <div className="space-y-1 mt-2">
                                {(item.referenceLinks || item.reference_links).map((link: string, linkIndex: number) => {
                                  const platformMatch = link.match(/^\[(.+?)\]\s*(.+)$/);
                                  const platform = platformMatch ? platformMatch[1] : '';
                                  const url = platformMatch ? platformMatch[2] : link;
                                  
                                  return (
                                    <div key={linkIndex} className="flex items-center gap-2 p-2 bg-gray-50 rounded text-xs">
                                      <LinkIcon className="h-3 w-3 text-gray-400 flex-shrink-0" />
                                      {platform && (
                                        <Badge variant="secondary" className="text-xs">{platform}</Badge>
                                      )}
                                      <a
                                        href={url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-blue-600 hover:underline truncate"
                                      >
                                        {url}
                                      </a>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                ) : (
                  // Legacy single-item display
                  <div className="space-y-3">
                    <div>
                      <Label className="text-sm font-semibold">Product Name</Label>
                      <p className="text-sm text-gray-700 mt-1">{selectedRequest.productName || selectedRequest.product_name}</p>
                    </div>
                    <div>
                      <Label className="text-sm font-semibold">Description</Label>
                      <p className="text-sm text-gray-700 mt-1">{selectedRequest.description}</p>
                    </div>
                    <div>
                      <Label className="text-sm font-semibold">Quantity</Label>
                      <p className="text-sm text-gray-700 mt-1">
                        {selectedRequest.quantity} {selectedRequest.quantityUnit || selectedRequest.quantity_unit}
                      </p>
                    </div>
                  </div>
                )}

                {/* Target Price (if specified) */}
                {selectedRequest.targetPrice && (
                  <div>
                    <Label className="text-sm font-semibold">Target Price</Label>
                    <p className="text-sm text-gray-700 mt-1">
                      ${selectedRequest.targetPrice}
                    </p>
                  </div>
                )}

                {(selectedRequest.status === 'Quoted' || selectedRequest.status === 'Waiting for Payment' || selectedRequest.status === 'Payment Received') && (
                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 space-y-3">
                    <h4 className="font-semibold text-purple-900">Quote Details ({selectedRequest.currency || 'USD'})</h4>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div>
                        <span className="text-gray-600">Total Product Cost:</span>
                        <p className="font-semibold">
                          $
                          {parseFloat(selectedRequest.quotedTotalAmount || selectedRequest.quoted_total_amount || 0).toFixed(2)}
                        </p>
                      </div>
                      <div>
                        <span className="text-gray-600">Sourcing Fee:</span>
                        <p className="font-semibold">
                          $
                          {parseFloat(selectedRequest.sourcingFee || selectedRequest.sourcing_fee || 0).toFixed(2)}
                        </p>
                      </div>
                      <div>
                        <span className="text-gray-600">Shipping Estimate:</span>
                        <p className="font-semibold">
                          $
                          {parseFloat(selectedRequest.shippingEstimate || selectedRequest.shipping_estimate || 0).toFixed(2)}
                        </p>
                      </div>
                    </div>
                    <div className="pt-2 border-t border-purple-300">
                      <div className="flex items-center justify-between">
                        <span className="font-semibold text-purple-900">Grand Total:</span>
                        <span className="text-xl font-bold text-purple-900">
                          $
                          {parseFloat(selectedRequest.grandTotal || selectedRequest.grand_total || 0).toFixed(2)}
                        </span>
                      </div>
                    </div>
                    {(selectedRequest.quoteNotes || selectedRequest.quote_notes) && (
                      <div>
                        <Label className="text-sm font-semibold">Quote Notes</Label>
                        <p className="text-sm text-gray-700 mt-1">{selectedRequest.quoteNotes || selectedRequest.quote_notes}</p>
                      </div>
                    )}
                  </div>
                )}

                {(selectedRequest.specialRequirements || selectedRequest.special_requirements) && (
                  <div>
                    <Label className="text-sm font-semibold">Special Requirements</Label>
                    <p className="text-sm text-gray-700 mt-1">{selectedRequest.specialRequirements || selectedRequest.special_requirements}</p>
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Edit Dialog (Customer can edit items but NOT prices) */}
        <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
          <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Edit Request</DialogTitle>
              <DialogDescription>
                You can edit product details but not the quoted prices
              </DialogDescription>
            </DialogHeader>
            {selectedRequest && (
              <div className="grid gap-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-productName">Product Name</Label>
                  <Input
                    id="edit-productName"
                    value={selectedRequest.productName}
                    onChange={(e) => setSelectedRequest({ ...selectedRequest, productName: e.target.value })}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="edit-description">Description</Label>
                  <Textarea
                    id="edit-description"
                    value={selectedRequest.description}
                    onChange={(e) => setSelectedRequest({ ...selectedRequest, description: e.target.value })}
                    rows={4}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="edit-quantity">Quantity</Label>
                    <Input
                      id="edit-quantity"
                      type="number"
                      value={selectedRequest.quantity}
                      onChange={(e) => setSelectedRequest({ ...selectedRequest, quantity: parseInt(e.target.value) })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="edit-unit">Unit</Label>
                    <Select
                      value={selectedRequest.quantityUnit}
                      onValueChange={(value) => setSelectedRequest({ ...selectedRequest, quantityUnit: value })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pcs">pcs</SelectItem>
                        <SelectItem value="kg">kg</SelectItem>
                        <SelectItem value="boxes">boxes</SelectItem>
                        <SelectItem value="sets">sets</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="edit-specialRequirements">Special Requirements</Label>
                  <Textarea
                    id="edit-specialRequirements"
                    value={selectedRequest.specialRequirements}
                    onChange={(e) => setSelectedRequest({ ...selectedRequest, specialRequirements: e.target.value })}
                    rows={3}
                  />
                </div>

                {/* Show locked prices */}
                {selectedRequest.status === 'Quoted' && (
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                    <p className="text-sm text-gray-600 mb-2">
                      <strong>Quoted Prices (Cannot be edited):</strong>
                    </p>
                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between">
                        <span>Grand Total:</span>
                        <span className="font-semibold">${parseFloat(selectedRequest.grandTotal || selectedRequest.grand_total || 0).toFixed(2)}</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
            <DialogFooter>
              <Button onClick={handleEditRequest}>Save Changes</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Quote Dialog (Admin only) */}
        <Dialog open={isQuoteDialogOpen} onOpenChange={setIsQuoteDialogOpen}>
          <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Send Quote</DialogTitle>
              <DialogDescription>
                Request #{selectedRequest?.requestNumber || selectedRequest?.request_number}
              </DialogDescription>
            </DialogHeader>
            {selectedRequest && (
              <div className="grid gap-4 py-4">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 space-y-2">
                  <div className="flex items-center gap-2">
                    <User className="h-4 w-4 text-blue-600" />
                    <span className="text-sm font-semibold text-blue-900">
                      {selectedRequest.customer_name || selectedRequest.customerName || 'Unknown'}
                    </span>
                    <Badge variant="secondary" className="text-xs">
                      {selectedRequest.customer_code || selectedRequest.customerCode || 'N/A'}
                    </Badge>
                  </div>
                  
                  {/* Show items list with target prices */}
                  {selectedRequest.items && selectedRequest.items.length > 0 ? (
                    <div className="text-sm text-blue-900">
                      <strong>Requesting {selectedRequest.items.length} item{selectedRequest.items.length !== 1 ? 's' : ''}:</strong>
                    </div>
                  ) : (
                    <p className="text-sm text-blue-900">
                      <strong>Requesting:</strong> {selectedRequest.quantity} {selectedRequest.quantityUnit || selectedRequest.quantity_unit} of {selectedRequest.productName || selectedRequest.product_name}
                    </p>
                  )}
                </div>

                {/* Per-Item Pricing Table */}
                {selectedRequest.items && selectedRequest.items.length > 0 ? (
                  <div className="space-y-3">
                    <Label className="text-sm font-semibold">Item Pricing</Label>
                    <div className="border rounded-lg overflow-hidden">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-3 py-2 text-left font-semibold">#</th>
                            <th className="px-3 py-2 text-left font-semibold">Product</th>
                            <th className="px-3 py-2 text-right font-semibold">Qty</th>
                            <th className="px-3 py-2 text-right font-semibold">Target Price</th>
                            <th className="px-3 py-2 text-right font-semibold">Your Quote ($)</th>
                          </tr>
                        </thead>
                        <tbody>
                          {selectedRequest.items.map((item: any, idx: number) => (
                            <tr key={idx} className="border-t">
                              <td className="px-3 py-3">{idx + 1}</td>
                              <td className="px-3 py-3">
                                <div className="font-medium">{item.productName || item.product_name}</div>
                                <div className="text-xs text-gray-500 truncate max-w-[200px]">
                                  {item.description}
                                </div>
                              </td>
                              <td className="px-3 py-3 text-right whitespace-nowrap">
                                {item.quantity} {item.quantityUnit || item.quantity_unit}
                              </td>
                              <td className="px-3 py-3 text-right">
                                {item.targetPrice || item.target_price ? (
                                  <span className="text-gray-600">${parseFloat(item.targetPrice || item.target_price).toFixed(2)}</span>
                                ) : (
                                  <span className="text-gray-400">—</span>
                                )}
                              </td>
                              <td className="px-3 py-3">
                                <Input
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  value={quoteData.itemPrices[idx] || ''}
                                  onChange={(e) => setQuoteData({
                                    ...quoteData,
                                    itemPrices: { ...quoteData.itemPrices, [idx]: e.target.value }
                                  })}
                                  placeholder="0.00"
                                  className="text-right"
                                />
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Label htmlFor="quote-pricePerUnit">Price Per Unit ($) *</Label>
                    <Input
                      id="quote-pricePerUnit"
                      type="number"
                      step="0.01"
                      value={quoteData.itemPrices[0] || ''}
                      onChange={(e) => setQuoteData({ ...quoteData, itemPrices: { 0: e.target.value } })}
                      placeholder="25.00"
                    />
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="quote-sourcingFee">Sourcing Fee ($)</Label>
                  <Input
                    id="quote-sourcingFee"
                    type="number"
                    step="0.01"
                    value={quoteData.sourcingFee}
                    onChange={(e) => setQuoteData({ ...quoteData, sourcingFee: e.target.value })}
                    placeholder="500.00"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="quote-shipping">Shipping Estimate ($)</Label>
                  <Input
                    id="quote-shipping"
                    type="number"
                    step="0.01"
                    value={quoteData.shippingEstimate}
                    onChange={(e) => setQuoteData({ ...quoteData, shippingEstimate: e.target.value })}
                    placeholder="800.00"
                  />
                </div>

                {/* Total Preview */}
                {Object.keys(quoteData.itemPrices).length > 0 && (
                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                    <h4 className="font-semibold text-purple-900 mb-3">Quote Summary</h4>
                    <div className="space-y-2 text-sm">
                      {/* Calculate total product cost */}
                      {(() => {
                        let totalProductCost = 0;
                        if (selectedRequest.items && selectedRequest.items.length > 0) {
                          selectedRequest.items.forEach((item: any, idx: number) => {
                            const price = parseFloat(quoteData.itemPrices[idx]) || 0;
                            const qty = parseInt(item.quantity) || 0;
                            totalProductCost += price * qty;
                          });
                        } else {
                          const price = parseFloat(quoteData.itemPrices[0]) || 0;
                          const qty = parseInt(selectedRequest.quantity) || 0;
                          totalProductCost = price * qty;
                        }
                        
                        return (
                          <>
                            <div className="flex justify-between">
                              <span>Total Product Cost:</span>
                              <span className="font-semibold">${totalProductCost.toFixed(2)}</span>
                            </div>
                            <div className="flex justify-between">
                              <span>Sourcing Fee:</span>
                              <span className="font-semibold">${(parseFloat(quoteData.sourcingFee) || 0).toFixed(2)}</span>
                            </div>
                            <div className="flex justify-between">
                              <span>Shipping Estimate:</span>
                              <span className="font-semibold">${(parseFloat(quoteData.shippingEstimate) || 0).toFixed(2)}</span>
                            </div>
                            <div className="pt-2 border-t border-purple-300 flex justify-between">
                              <span className="font-bold text-purple-900">Grand Total:</span>
                              <span className="text-lg font-bold text-purple-900">
                                ${(
                                  totalProductCost +
                                  (parseFloat(quoteData.sourcingFee) || 0) +
                                  (parseFloat(quoteData.shippingEstimate) || 0)
                                ).toFixed(2)}
                              </span>
                            </div>
                          </>
                        );
                      })()}
                    </div>
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="quote-notes">Quote Notes</Label>
                  <Textarea
                    id="quote-notes"
                    value={quoteData.quoteNotes}
                    onChange={(e) => setQuoteData({ ...quoteData, quoteNotes: e.target.value })}
                    placeholder="Additional information about the quote..."
                    rows={3}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="quote-validUntil">Quote Valid Until</Label>
                  <Input
                    id="quote-validUntil"
                    type="date"
                    value={quoteData.quoteValidUntil}
                    onChange={(e) => setQuoteData({ ...quoteData, quoteValidUntil: e.target.value })}
                  />
                </div>
              </div>
            )}
            <DialogFooter>
              <Button onClick={handleSendQuote}>Send Quote to Customer</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Status Update Dialog (Admin/Warehouse) */}
        <Dialog open={isStatusDialogOpen} onOpenChange={setIsStatusDialogOpen}>
          <DialogContent className="sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle>Update Request Status</DialogTitle>
              <DialogDescription>
                Change the status of request {selectedForStatus?.requestNumber || selectedForStatus?.request_number}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="new-status">New Status</Label>
                <Select value={newStatus} onValueChange={setNewStatus}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Pending">Pending</SelectItem>
                    <SelectItem value="Under Review">Under Review</SelectItem>
                    <SelectItem value="Quoted">Quoted</SelectItem>
                    <SelectItem value="Waiting for Payment">Waiting for Payment</SelectItem>
                    <SelectItem value="Payment Received">Payment Received</SelectItem>
                    <SelectItem value="Order Placed with Supplier">Order Placed with Supplier</SelectItem>
                    <SelectItem value="In Production">In Production</SelectItem>
                    <SelectItem value="Ready for Shipment">Ready for Shipment</SelectItem>
                    <SelectItem value="Shipment Created">Shipment Created</SelectItem>
                    <SelectItem value="Completed">Completed</SelectItem>
                    <SelectItem value="Cancelled">Cancelled</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="status-note">Status Note (Optional)</Label>
                <Textarea
                  id="status-note"
                  value={statusNote}
                  onChange={(e) => setStatusNote(e.target.value)}
                  placeholder="Add a note about this status change..."
                  rows={3}
                />
              </div>

              {/* Payment Tracking for "Payment Received" status */}
              {newStatus === 'Payment Received' && selectedForStatus && (
                <div className="border-2 border-blue-200 rounded-lg p-4 bg-blue-50 space-y-3">
                  <h4 className="font-semibold text-blue-900 flex items-center gap-2">
                    <DollarSign className="h-5 w-5" />
                    Payment Information
                  </h4>
                  
                  <div className="space-y-2">
                    <Label htmlFor="amount-paid">Amount Received *</Label>
                    <Input
                      id="amount-paid"
                      type="number"
                      step="0.01"
                      min="0"
                      value={amountPaid}
                      onChange={(e) => setAmountPaid(e.target.value)}
                      placeholder="Enter amount received"
                      className="bg-white"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="payment-notes">Payment Notes</Label>
                    <Textarea
                      id="payment-notes"
                      value={paymentNotes}
                      onChange={(e) => setPaymentNotes(e.target.value)}
                      placeholder="Payment method, transaction ID, etc..."
                      rows={2}
                      className="bg-white"
                    />
                  </div>

                  {/* Payment Status Indicator */}
                  {amountPaid && selectedForStatus.grand_total && (
                    <div className="mt-3 p-3 bg-white rounded border">
                      <div className="flex justify-between items-center text-sm">
                        <span className="text-gray-600">Grand Total:</span>
                        <span className="font-bold">
                          $
                          {parseFloat(selectedForStatus.grand_total || selectedForStatus.grandTotal || 0).toFixed(2)}
                        </span>
                      </div>
                      <div className="flex justify-between items-center text-sm mt-1">
                        <span className="text-gray-600">Amount Received:</span>
                        <span className="font-bold text-green-600">
                          $
                          {parseFloat(amountPaid || 0).toFixed(2)}
                        </span>
                      </div>
                      <div className="flex justify-between items-center text-sm mt-1 pt-2 border-t">
                        <span className="text-gray-600">Payment Status:</span>
                        <Badge className={
                          parseFloat(amountPaid || 0) >= parseFloat(selectedForStatus.grand_total || selectedForStatus.grandTotal || 0)
                            ? 'bg-green-100 text-green-800'
                            : 'bg-yellow-100 text-yellow-800'
                        }>
                          {parseFloat(amountPaid || 0) >= parseFloat(selectedForStatus.grand_total || selectedForStatus.grandTotal || 0)
                            ? 'Fully Paid'
                            : 'Partial Payment'}
                        </Badge>
                      </div>
                      {parseFloat(amountPaid || 0) < parseFloat(selectedForStatus.grand_total || selectedForStatus.grandTotal || 0) && (
                        <div className="flex justify-between items-center text-sm mt-1 text-orange-600">
                          <span>Remaining:</span>
                          <span className="font-bold">
                            $
                            {(parseFloat(selectedForStatus.grand_total || selectedForStatus.grandTotal || 0) - parseFloat(amountPaid || 0)).toFixed(2)}
                          </span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {selectedForStatus && (
                <div className="border rounded-lg p-3 bg-gray-50">
                  <h4 className="font-medium text-sm mb-2">Current Status</h4>
                  <Badge className={getStatusColor(selectedForStatus.status)}>
                    {selectedForStatus.status}
                  </Badge>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button onClick={handleUpdateStatus}>
                <RotateCw className="h-4 w-4 mr-2" />
                Update Status
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Create Shipment from Sourcing Request Dialog */}
        <Dialog open={isCreateShipmentDialogOpen} onOpenChange={setIsCreateShipmentDialogOpen}>
          <DialogContent className="sm:max-w-[900px] max-h-[90vh] overflow-hidden flex flex-col">
            <DialogHeader>
              <DialogTitle>Create Shipment</DialogTitle>
              <DialogDescription>
                Creating shipment for sourcing request #{selectedRequestForShipment?.requestNumber || selectedRequestForShipment?.request_number}
              </DialogDescription>
            </DialogHeader>
            <div className="flex-1 overflow-y-auto px-1">
              {selectedRequestForShipment && (
                <MultiItemShipmentForm
                  customers={apiCustomers || []}
                  trips={apiTrips || []}
                  warehouses={warehouses}
                  onSubmit={handleCreateShipmentFromRequest}
                  isSubmitting={false}
                  showTripSelection={true}
                  defaultStatus="Processing"
                  initialData={{
                    customerCode: selectedRequestForShipment.customer_code || selectedRequestForShipment.customerCode,
                    customerName: selectedRequestForShipment.customer_name || selectedRequestForShipment.customerName,
                    items: (selectedRequestForShipment.items || []).map((item: any) => ({
                      itemNumber: item.itemNumber || 0,
                      description: item.description || item.productName || item.product_name || '',
                      quantity: item.quantity || 0,
                      quantityUnit: item.quantityUnit || item.quantity_unit || 'pcs',
                      weight: 0, // User must fill
                      volume: 0, // User must fill
                      boxes: 0   // User must fill
                    }))
                  }}
                />
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* Edit Quotation Dialog (Full Edit: Items, Pictures, Prices) */}
        <Dialog open={isEditQuotationDialogOpen} onOpenChange={setIsEditQuotationDialogOpen}>
          <DialogContent className="sm:max-w-[900px] max-h-[90vh] overflow-hidden flex flex-col">
            <DialogHeader>
              <DialogTitle>Edit Quotation</DialogTitle>
              <DialogDescription>
                Edit items, descriptions, quantities, pictures, and all details for request #{selectedRequest?.requestNumber || selectedRequest?.request_number}
              </DialogDescription>
            </DialogHeader>
            <div className="flex-1 overflow-y-auto px-1">
              {selectedRequest && (
                <MultiItemSourcingForm
                  onSubmit={handleEditQuotation}
                  isSubmitting={false}
                  initialData={{
                    items: selectedRequest.items || [],
                    notes: selectedRequest.notes || selectedRequest.special_requirements || '',
                    currency: 'USD'
                  }}
                />
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* Print/Download Dialog */}
        <Dialog open={isPrintDialogOpen} onOpenChange={setIsPrintDialogOpen}>
          <DialogContent className="sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle>Print Request & Quote</DialogTitle>
              <DialogDescription>
                Generate a printable document for {selectedRequest?.requestNumber || selectedRequest?.request_number}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="flex flex-col gap-3">
                <Button onClick={handlePrintRequest} className="w-full">
                  <Printer className="h-4 w-4 mr-2" />
                  Print Document
                </Button>
                <p className="text-sm text-gray-600 text-center">
                  Document includes: Request details, customer information, quote (if available), and QR code
                </p>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Pay with Wallet Dialog */}
        {selectedRequestForPayment && (
          <PayWithWalletDialog
            open={showPayDialog}
            onOpenChange={setShowPayDialog}
            type="sourcing"
            referenceNumber={selectedRequestForPayment.requestNumber || selectedRequestForPayment.request_number}
            amount={parseFloat(selectedRequestForPayment.grandTotal || selectedRequestForPayment.grand_total || 0)}

            onSuccess={() => {
              window.location.reload();
            }}
          />
        )}
      </div>
    </Layout>
  );
}
